'use client'

/**
 * The header's Saved + Compare buttons with live counts — deliberately LIGHT.
 * It imports only the localStorage store; the panels (product rows, images,
 * prices) load on the first click via next/dynamic, so no route pays for the
 * homepage package in its first-load JS (bundle-budget, 09/03/2026: the
 * previous direct import grew every route's First Load JS past its budget).
 * The two icons are the canvas sprite's `i-compare` and `i-heart` paths inline,
 * because the sprite itself is mounted only on the homepage.
 */
import dynamic from 'next/dynamic'
import * as React from 'react'
import { useSavedCompare } from '@/components/homepage-2026/saved-compare-store'

/**
 * FRIDGE-1107 (owner, on a phone: "Takes too Long to Open"). The panel chunk used to be
 * fetched on the FIRST tap, so a phone waited for a download and parse before anything
 * painted. It still stays out of every route's first-load JS (the 09/03 bundle rule), but it
 * is now fetched once the page is idle — and on the finger's first touch of either button, for
 * a visitor who taps before idle. Once it has arrived the panel renders synchronously from the
 * loaded module (no lazy boundary, no suspended frame); before that, next/dynamic is the
 * fallback, exactly as it was.
 */
const loadPanel = () => import('@/components/homepage-2026/saved-compare')
const SavedComparePanel = dynamic(() => loadPanel().then((m) => m.SavedComparePanel), { ssr: false })
type PanelComponent = typeof import('@/components/homepage-2026/saved-compare')['SavedComparePanel']
let loadedPanel: PanelComponent | null = null
let warming: Promise<unknown> | null = null
export function warmSavedComparePanel(): Promise<unknown> {
  if (!warming) warming = loadPanel().then((m) => { loadedPanel = m.SavedComparePanel }).catch(() => { warming = null })
  return warming
}

/**
 * FRIDGE-1153 (owner's choice, 09/24/2026: "Move Compare into menu"). Below
 * 640px the header's Compare button is hidden (canvas.css `.hdr-compare`) so
 * the logo gets the row's width back; the phone menu drawer opens the SAME
 * panel through this event, so there is still one panel and one store.
 */
export const OPEN_COMPARE_EVENT = 'fridge:open-compare'
export function openHeaderCompare() {
  window.dispatchEvent(new Event(OPEN_COMPARE_EVENT))
}

function CompareIcon() {
  return (
    <svg className="i20" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 4v13M7 17l-3-3M7 17l3-3M17 20V7M17 7l-3 3M17 7l3 3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
function HeartIcon({ style }: { style?: React.CSSProperties }) {
  return (
    <svg className="i20" viewBox="0 0 24 24" aria-hidden="true" style={style}>
      <path d="M12 20.5S4 15 4 9.6A4.4 4.4 0 0112 6a4.4 4.4 0 018 3.6C20 15 12 20.5 12 20.5z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  )
}

export function HeaderSavedCompare() {
  const sc = useSavedCompare()
  const [panel, setPanel] = React.useState<'sav' | 'cmp' | null>(null)
  // Warm the panel chunk when the main thread is idle (or 2 s after mount where
  // requestIdleCallback does not exist — Safari).
  React.useEffect(() => {
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void }
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(() => { void warmSavedComparePanel() }, { timeout: 4000 })
      return () => w.cancelIdleCallback?.(id)
    }
    const t = window.setTimeout(() => { void warmSavedComparePanel() }, 2000)
    return () => window.clearTimeout(t)
  }, [])
  // A panel, once opened, stays MOUNTED and is only hidden when closed: a repeat open is a
  // style change, not a rebuild of every row and thumbnail. The component (the loaded module,
  // or next/dynamic if the chunk had not arrived yet) is chosen at that first open and kept, so
  // the module arriving mid-view never swaps the component type and remounts it.
  const [Panel, setPanelComponent] = React.useState<React.ComponentType<{ kind: 'sav' | 'cmp'; onClose: () => void }> | null>(null)
  const [mounted, setMounted] = React.useState<{ sav: boolean; cmp: boolean }>({ sav: false, cmp: false })
  const toggle = (kind: 'sav' | 'cmp') => {
    if (!Panel) { const chosen = loadedPanel ?? SavedComparePanel; setPanelComponent(() => chosen) }
    if (!mounted[kind]) setMounted((m) => ({ ...m, [kind]: true }))
    setPanel(panel === kind ? null : kind)
  }
  const close = React.useCallback(() => setPanel(null), [])
  // The drawer's Compare item (phones) opens the compare panel; it never
  // toggles an open panel shut. Functional updates only, so the listener needs
  // no render-time state.
  React.useEffect(() => {
    const onOpen = () => {
      setPanelComponent((prev) => prev ?? (loadedPanel ?? SavedComparePanel))
      setMounted((m) => (m.cmp ? m : { ...m, cmp: true }))
      setPanel('cmp')
    }
    window.addEventListener(OPEN_COMPARE_EVENT, onOpen)
    return () => window.removeEventListener(OPEN_COMPARE_EVENT, onOpen)
  }, [])
  const badge = (n: number) => (n > 0 ? <span className="hdrbadge">{n}</span> : null)
  return (
    // NO `position: relative` here (FRIDGE-873). The panel is absolutely
    // positioned; anchoring it to this ~120px-wide wrapper put it partly
    // off-screen once the buttons stopped being the header's right-most element,
    // and the header row's own `overflow-hidden` would clip it. With no
    // positioned ancestor between it and <header> (which is `sticky`, and so is
    // a containing block), `.hdrpanel`'s `top: 100%` sits it under the whole
    // header bar and its `right` is measured from the VIEWPORT edge — on screen
    // at 320px and at 1440px alike, and outside the clipping row.
    <div className="hp26 hdr-icons" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <button
        type="button"
        className="hdrbtn hdr-compare"
        style={{ color: '#000000', ...(panel === 'cmp' ? { background: '#f2f4f7' } : {}) }}
        onPointerDown={() => { void warmSavedComparePanel() }}
        onClick={() => toggle('cmp')}
        aria-label="Open your compare list"
        aria-expanded={panel === 'cmp'}
      >
        <span style={{ position: 'relative', display: 'inline-flex' }}>
          <CompareIcon />
          {badge(sc.compare.length)}
        </span>
        <span className="hdr-label">Compare</span>
      </button>
      <button
        type="button"
        className="hdrbtn"
        style={panel === 'sav' ? { background: '#f2f4f7', color: '#0469f0' } : undefined}
        onPointerDown={() => { void warmSavedComparePanel() }}
        onClick={() => toggle('sav')}
        aria-label="Open your saved list"
        aria-expanded={panel === 'sav'}
      >
        <span style={{ position: 'relative', display: 'inline-flex' }}>
          <HeartIcon style={sc.saved.length ? { color: '#dc2626' } : undefined} />
          {badge(sc.saved.length)}
        </span>
        <span className="hdr-label">Saved</span>
      </button>
      {Panel
        ? (['sav', 'cmp'] as const).map((k) =>
            mounted[k] ? (
              // display:contents keeps the wrapper box-less, so `.hdrpanel` still positions
              // against <header> exactly as before (FRIDGE-873); display:none removes a closed
              // panel from layout and the accessibility tree.
              <div key={k} style={{ display: panel === k ? 'contents' : 'none' }}>
                <Panel kind={k} onClose={close} />
              </div>
            ) : null,
          )
        : null}
    </div>
  )
}
